import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaEye, FaFilePdf, FaSpinner } from 'react-icons/fa';
import axios from 'axios';
import FlipbookViewer from './FlipbookViewer';
import './BrochuresManager.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

const BrochuresManager = ({ vcardId, brochures = [], onChange, isPremium = false, onVCardSaveNeeded }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewingBrochure, setViewingBrochure] = useState(null);
  const [loadedBrochures, setLoadedBrochures] = useState(brochures);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    file: null,
  });

  // Load existing brochures when vcardId is provided
  useEffect(() => {
    const fetchBrochures = async () => {
      if (vcardId && brochures.length === 0 && loadedBrochures.length === 0) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API_URL}/vcards/${vcardId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.brochures && response.data.brochures.length > 0) {
            setLoadedBrochures(response.data.brochures);
            onChange(response.data.brochures);
          }
        } catch (error) {
          console.error('Error fetching brochures:', error);
        }
      } else if (brochures.length > 0 && loadedBrochures.length === 0) {
        setLoadedBrochures(brochures);
      }
    };
    fetchBrochures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vcardId, brochures.length, loadedBrochures.length]);
  
  // Update loadedBrochures when brochures prop changes
  useEffect(() => {
    if (brochures.length > 0) {
      setLoadedBrochures(brochures);
    }
  }, [brochures]);

  // Use loadedBrochures or props brochures
  const displayBrochures = loadedBrochures.length > 0 ? loadedBrochures : brochures;

  if (!isPremium) {
    return (
      <div className="brochures-manager premium-only">
        <h3>Brochures / Flipbook</h3>
        <div className="premium-badge">
          <span>Premium Feature</span>
        </div>
        <p>Upload PDF files and display them in an interactive flipbook format. Upgrade to premium to unlock this feature.</p>
      </div>
    );
  }

  const handleFileChange = (e) => {
    setFormData({ ...formData, file: e.target.files[0] });
  };

  const handleUpload = async () => {
    if (!formData.file || !formData.title.trim()) {
      alert('Please provide a title and select a file');
      return;
    }

    // If vCardId doesn't exist, we need to save the vCard first
    let currentVCardId = vcardId;
    if (!currentVCardId && onVCardSaveNeeded) {
      try {
        // Call the callback to save the vCard first
        const savedVCard = await onVCardSaveNeeded();
        if (savedVCard && savedVCard._id) {
          currentVCardId = savedVCard._id;
        } else {
          alert('Please save the vCard first before adding brochures.');
          return;
        }
      } catch (error) {
        console.error('Error saving vCard:', error);
        alert('Failed to save vCard. Please save the vCard first before adding brochures.');
        return;
      }
    }

    if (!currentVCardId) {
      alert('vCard must be saved before adding brochures. Please save the vCard first.');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Upload file
      const uploadFormData = new FormData();
      uploadFormData.append('file', formData.file);

      // Set longer timeout for large PDF uploads (5 minutes)
      const uploadResponse = await axios.post(
        `${API_URL}/vcards/${currentVCardId}/brochures/upload`,
        uploadFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          timeout: 300000, // 5 minutes timeout for large PDFs with many pages
        }
      );

      // Log upload response for debugging
      console.log('Upload response:', uploadResponse.data);

      // Validate upload response
      if (!uploadResponse.data || !uploadResponse.data.fileUrl) {
        throw new Error('Invalid upload response: missing fileUrl');
      }

      // Add brochure to vCard
      // Use original S3 URLs/keys for database storage (not presigned URLs which expire)
      const brochureData = {
        title: formData.title,
        description: formData.description,
        fileUrl: uploadResponse.data.fileUrl, // Original S3 URL/key (not presigned)
        thumbnail: uploadResponse.data.thumbnail || uploadResponse.data.thumbnailPresigned, // Use presigned if original not available
        type: uploadResponse.data.type || 'pdf',
        pages: uploadResponse.data.pages || uploadResponse.data.pagesPresigned || [], // Original S3 URLs/keys (not presigned)
        pageCount: uploadResponse.data.pageCount || 0,
        processingStatus: uploadResponse.data.processingStatus || 'pending',
      };

      console.log('Brochure data to add:', brochureData);

      const addResponse = await axios.post(
        `${API_URL}/vcards/${currentVCardId}/brochures`,
        brochureData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log('Add brochure response:', addResponse.data);

      const newBrochure = addResponse.data;
      const updatedBrochures = [...displayBrochures, newBrochure];
      setLoadedBrochures(updatedBrochures);
      onChange(updatedBrochures);
      setFormData({ title: '', description: '', file: null });
      setShowAddForm(false);
      
      // Show success message
      if (newBrochure.pages && newBrochure.pages.length > 0) {
        alert(`Brochure uploaded successfully! ${newBrochure.pageCount} pages converted.`);
      } else if (newBrochure.processingStatus === 'pending' || newBrochure.processingStatus === 'processing') {
        alert('Brochure uploaded successfully! Processing pages in the background. Pages will appear when ready.');
        // Start polling for status
        pollBrochureStatus(currentVCardId, newBrochure._id);
      } else if (newBrochure.processingStatus === 'failed') {
        alert(`Brochure uploaded, but PDF processing failed: ${newBrochure.processingError || 'Unknown error'}. The PDF file is available for download, but flipbook view may not work.`);
      } else {
        alert('Brochure uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading brochure:', error);
      alert(error.response?.data?.error || 'Failed to upload brochure');
    } finally {
      setUploading(false);
    }
  };

  // Poll for brochure processing status
  const pollBrochureStatus = async (vcardId, brochureId, retries = 0, maxRetries = 60) => {
    if (retries >= maxRetries) {
      console.log('Max polling retries reached');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/vcards/${vcardId}/brochures/${brochureId}/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.processingStatus === 'completed') {
        // Refresh brochure list
        const vcardResponse = await axios.get(`${API_URL}/vcards/${vcardId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (vcardResponse.data.brochures) {
          setLoadedBrochures(vcardResponse.data.brochures);
          onChange(vcardResponse.data.brochures);
          // Show success message
          if (response.data.pageCount > 0) {
            console.log(`Brochure processing completed! ${response.data.pageCount} pages converted.`);
          }
        }
      } else if (response.data.processingStatus === 'failed') {
        // Refresh to get error details
        const vcardResponse = await axios.get(`${API_URL}/vcards/${vcardId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (vcardResponse.data.brochures) {
          setLoadedBrochures(vcardResponse.data.brochures);
          onChange(vcardResponse.data.brochures);
        }
        console.error('Brochure processing failed:', response.data.processingError);
      } else {
        // Still processing, poll again after 3 seconds
        setTimeout(() => {
          pollBrochureStatus(vcardId, brochureId, retries + 1, maxRetries);
        }, 3000);
      }
    } catch (error) {
      console.error('Error polling brochure status:', error);
      // Retry after 3 seconds
      setTimeout(() => {
        pollBrochureStatus(vcardId, brochureId, retries + 1, maxRetries);
      }, 3000);
    }
  };

  const handleDelete = async (brochureId) => {
    if (!window.confirm('Are you sure you want to delete this brochure?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/vcards/${vcardId}/brochures/${brochureId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const updatedBrochures = displayBrochures.filter(b => b._id !== brochureId);
      setLoadedBrochures(updatedBrochures);
      onChange(updatedBrochures);
    } catch (error) {
      console.error('Error deleting brochure:', error);
      alert(error.response?.data?.error || 'Failed to delete brochure');
    }
  };

  return (
    <div className="brochures-manager">
      <div className="brochures-header">
        <h3>Brochures / Flipbook</h3>
        <button onClick={() => setShowAddForm(true)} className="btn btn-primary btn-sm">
          <FaPlus /> Add Brochure
        </button>
      </div>

      {showAddForm && (
        <div className="brochure-form">
          <h4>Add Brochure</h4>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
            />
          </div>
          <div className="form-group">
            <label>File (PDF) *</label>
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              required
            />
            {formData.file && (
              <p className="file-info">
                Selected: {formData.file.name} ({(formData.file.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
          </div>
          <div className="form-actions">
            <button
              onClick={handleUpload}
              disabled={uploading || !formData.file || !formData.title.trim()}
              className="btn btn-primary"
            >
              {uploading ? 'Uploading...' : 'Upload & Add'}
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setFormData({ title: '', description: '', file: null });
              }}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {displayBrochures.length === 0 && !showAddForm && (
        <div className="empty-state">
          <p>No brochures added yet. Click "Add Brochure" to upload a PDF file.</p>
        </div>
      )}

      <div className="brochures-list">
        {displayBrochures.map((brochure) => (
          <div key={brochure._id || brochure.title} className="brochure-item">
            {brochure.thumbnail ? (
              <div className="brochure-thumbnail">
                <img 
                  src={brochure.thumbnail.startsWith('http') 
                    ? brochure.thumbnail 
                    : `${BASE_URL}${brochure.thumbnail.startsWith('/') ? brochure.thumbnail : `/${brochure.thumbnail}`}`} 
                  alt={brochure.title}
                  onError={(e) => {
                    console.error('Brochure thumbnail failed to load:', e.target.src);
                    console.error('Brochure data:', brochure);
                    e.target.style.display = 'none';
                    // Show placeholder on error
                    const placeholder = e.target.parentElement.querySelector('.thumbnail-placeholder');
                    if (placeholder) {
                      placeholder.style.display = 'flex';
                    }
                  }}
                  onLoad={() => {
                    console.log('Brochure thumbnail loaded successfully:', brochure.thumbnail);
                  }}
                />
                <div className="thumbnail-placeholder" style={{ display: 'none' }}>
                  <FaFilePdf />
                </div>
              </div>
            ) : (
              <div className={`brochure-thumbnail placeholder ${brochure.processingStatus === 'processing' ? 'processing' : ''}`}>
                {brochure.processingStatus === 'processing' ? (
                  <>
                    <FaSpinner className="spinning" />
                    <span style={{ fontSize: '0.8em', marginTop: '5px' }}>Processing...</span>
                  </>
                ) : (
                  <FaFilePdf />
                )}
              </div>
            )}
            <div className="brochure-content">
              <h4>{brochure.title}</h4>
              {brochure.description && <p>{brochure.description}</p>}
              <div className="brochure-meta">
                <span className="brochure-type">{brochure.type.toUpperCase()}</span>
                {brochure.pageCount > 0 && (
                  <span className="brochure-pages">{brochure.pageCount} pages</span>
                )}
              </div>
            </div>
            <div className="brochure-actions">
              {brochure.processingStatus === 'processing' ? (
                <span className="processing-indicator" title="Processing pages...">
                  <FaSpinner className="spinning" style={{ animation: 'spin 1s linear infinite', marginRight: '5px' }} />
                  Processing...
                </span>
              ) : brochure.pages && brochure.pages.length > 0 ? (
                <button
                  onClick={() => setViewingBrochure(brochure)}
                  className="btn-icon"
                  title="View Flipbook"
                >
                  <FaEye /> View
                </button>
              ) : brochure.processingStatus === 'failed' ? (
                <span className="no-pages-warning" title={brochure.processingError || 'PDF processing failed'}>
                  Failed
                </span>
              ) : (
                <span className="no-pages-warning" title="PDF processing failed or not available">
                  No pages
                </span>
              )}
              <button
                onClick={() => handleDelete(brochure._id)}
                className="btn-icon btn-danger"
                title="Delete"
              >
                <FaTrash />
              </button>
            </div>
          </div>
        ))}
      </div>

      {viewingBrochure && (
        <FlipbookViewer
          brochure={viewingBrochure}
          onClose={() => setViewingBrochure(null)}
        />
      )}
    </div>
  );
};

export default BrochuresManager;

